import { SecondaryNavigation } from '@reapitform/dashboard-ui'
import { DocumentActionButtons } from './document-action-buttons'
import { useNavigate, useParams } from 'react-router'
import { useAgreementState } from '../../use-agreement-state'
import React from 'react'
import {
  WarningUnsavedChangesModal,
  SignAndCompleteModal,
  WithdrawModal,
  ResendLinkModal,
  ChangeExpiryModal,
  SendForSigningModal,
  UnableToSendModal,
} from '../../../../components/modals'
import { useDocuments } from '../../client-provider'
import { useQueryClient } from '@tanstack/react-query'
import { getAgreementDetailQueryKey } from '../../../../../core/api/documents'

export const CreateFormSecondaryNav: React.FC = () => {
  const queryClient = useQueryClient()
  const documents = useDocuments()
  const navigate = useNavigate()

  const { documentId: documentIdParams } = useParams<string>()
  const documentId: string[] | undefined = documentIdParams ? [documentIdParams] : undefined

  const { state, setIsEditing } = useAgreementState()
  const [isOpenSendForSigningModal, setIsOpenSendForSigningModal] = React.useState<boolean>(false)
  const [isOpenChangeExpiryModal, setIsOpenChangeExpiryModal] = React.useState<boolean>(false)
  const [isOpenResendLinkModal, setIsOpenResendLinkModal] = React.useState<boolean>(false)
  const [isOpenWithdrawModal, setIsOpenWithdrawModal] = React.useState<boolean>(false)
  const [isOpenSignAndCompleteModal, setIsOpenSignAndCompleteModal] = React.useState<boolean>(false)
  const [isOpenUnableToSignModal, setIsOpenUnableToSignModal] = React.useState<boolean>(false)

  const [isShowUnsavedChangesWarning, setIsShowUnsavedChangesWarning] = React.useState<boolean>(false)
  const currentAction = React.useRef<() => void | undefined>()

  const handleSendForSigning = () => {
    if (state.hasError === undefined) return
    if (state.hasError) return setIsOpenUnableToSignModal(true)
    setIsOpenSendForSigningModal(true)
  }

  const showUnsavedChangesWarning = (callbackAction?: () => void) => {
    currentAction.current = callbackAction
    setIsShowUnsavedChangesWarning(true)
  }

  const handleActionClick = (callbackAction?: () => void) => () => {
    if (state.isEditing) return showUnsavedChangesWarning(callbackAction)
    callbackAction?.()
  }

  const handleDiscardChangesAndContinue = () => {
    setIsShowUnsavedChangesWarning(false)
    setIsEditing(false)
    currentAction.current?.()
  }

  return (
    <>
      <SecondaryNavigation
        title={documents?.headerTitle}
        exitBtnText="Exit"
        onExit={() => navigate('/agreement')}
        actionButtons={
          <DocumentActionButtons
            key="action-button"
            onSendForSigning={handleActionClick(handleSendForSigning)}
            onChangeExpiry={handleActionClick(() => setIsOpenChangeExpiryModal(true))}
            onResendlink={handleActionClick(() => setIsOpenResendLinkModal(true))}
            onWithdraw={handleActionClick(() => setIsOpenWithdrawModal(true))}
            onComplete={handleActionClick(() => setIsOpenSignAndCompleteModal(true))}
            isLoading={!state.isFinishedLoadSections}
            status={documents?.status}
          />
        }
      />
      <SendForSigningModal
        agreementId={documentId}
        isOpen={isOpenSendForSigningModal}
        onClose={() => setIsOpenSendForSigningModal(false)}
        onFinish={() => queryClient.invalidateQueries({ queryKey: getAgreementDetailQueryKey(documentIdParams) })}
      />
      <UnableToSendModal
        agreementStatus={documents?.status}
        isOpen={isOpenUnableToSignModal}
        onClose={() => setIsOpenUnableToSignModal(false)}
      />
      <ChangeExpiryModal
        agreementId={documentId}
        isOpen={isOpenChangeExpiryModal}
        onClose={() => setIsOpenChangeExpiryModal(false)}
        onFinish={() => queryClient.invalidateQueries({ queryKey: getAgreementDetailQueryKey(documentIdParams) })}
      />
      <ResendLinkModal
        agreementId={documentId}
        isOpen={isOpenResendLinkModal}
        onClose={() => setIsOpenResendLinkModal(false)}
        onFinish={() => queryClient.invalidateQueries({ queryKey: getAgreementDetailQueryKey(documentIdParams) })}
      />
      <WithdrawModal
        agreementId={documentId}
        isOpen={isOpenWithdrawModal}
        onClose={() => setIsOpenWithdrawModal(false)}
        onFinish={() => queryClient.invalidateQueries({ queryKey: getAgreementDetailQueryKey(documentIdParams) })}
      />
      <SignAndCompleteModal
        agreementId={documentId}
        isOpen={isOpenSignAndCompleteModal}
        onClose={() => setIsOpenSignAndCompleteModal(false)}
        onFinish={() => queryClient.invalidateQueries({ queryKey: getAgreementDetailQueryKey(documentIdParams) })}
      />
      <WarningUnsavedChangesModal
        isOpen={isShowUnsavedChangesWarning}
        onClose={() => setIsShowUnsavedChangesWarning(false)}
        onContinue={handleDiscardChangesAndContinue}
      />
    </>
  )
}
